import { Slot } from 'radix-ui';
import { cva, type VariantProps } from 'class-variance-authority';
import type { IconName } from 'lucide-react/dynamic';
import { forwardRef } from 'react';
import { cn } from '@/lib/utils';
import { Icon } from './icon';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[12px] text-sm font-semibold transition-[opacity,transform,background-color,border-color,color] duration-200 ease-[cubic-bezier(.2,.8,.2,1)] active:scale-[.97] disabled:pointer-events-none disabled:opacity-40 [&_svg]:pointer-events-none [&_svg:not([class*="size-"])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
  {
    variants: {
      variant: {
        default: 'border border-brand-fill bg-brand-fill text-white hover:opacity-90',
        destructive:
          'bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'border border-line-2 bg-transparent text-brand hover:border-brand/60 hover:bg-surface-2',
        danger:
          'border border-[rgba(229,72,77,.45)] bg-transparent text-[#E5484D] hover:bg-[rgba(229,72,77,.08)]',
        primary:
          'bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-1 focus:ring-primary-500 text-white disabled:bg-primary-300 disabled:text-white',
        secondary:
          'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        third:
          'bg-third-600 hover:bg-third-700 focus:outline-none focus:ring-1 focus:ring-third-500 text-white disabled:bg-third-300 disabled:text-white',
        ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
        none: 'bg-transparent hover:bg-gray-200 dark:hover:bg-gray-700 focus:outline-none text-black dark:text-gray-100 disabled:bg-gray-100 disabled:dark:bg-gray-800 disabled:text-gray-400 disabled:dark:text-gray-600',
        success:
          'bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-1 focus:ring-green-500 text-white disabled:bg-green-300 disabled:text-white',
        warning:
          'bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-1 focus:ring-yellow-500 text-white disabled:bg-yellow-300 disabled:text-white',
        info: 'bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-500 text-white disabled:bg-blue-300 disabled:dark:bg-blue-400 disabled:text-white',
        error:
          'bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-1 focus:ring-red-500 text-white disabled:bg-red-300 disabled:text-white',
        green:
          'bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-1 focus:ring-green-500 text-white disabled:bg-green-300 disabled:text-white',
        blue: 'bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-500 text-white disabled:bg-blue-300 disabled:dark:bg-blue-400 disabled:text-white',
        purple:
          'bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-1 focus:ring-purple-500 text-white disabled:bg-purple-300 disabled:text-white',
        orange:
          'bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-1 focus:ring-orange-500 text-white disabled:bg-orange-300 disabled:text-white',
        yellow:
          'bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-1 focus:ring-yellow-500 text-white disabled:bg-yellow-300 disabled:text-white',
      },
      size: {
        default: 'h-11 px-5',
        xs: 'h-7 px-2 py-1 text-xs',
        sm: 'h-9 gap-1.5 px-3.5 text-xs',
        md: 'h-11 px-5',
        lg: 'h-12 px-5',
        xl: 'h-12 px-8',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    icon?: IconName;
    iconPosition?: 'left' | 'right';
  };

const Button = ({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  children,
  icon,
  iconPosition = 'left',
  ...props
}: ButtonProps) => {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    >
      {icon && iconPosition === 'left' && (
        <Icon name={icon} className={cn(size === 'icon' ? 'mx-auto' : 'ml-2')} />
      )}
      {children}
      {icon && iconPosition === 'right' && (
        <Icon name={icon} className={cn(size === 'icon' ? 'mx-auto' : 'mr-2')} />
      )}
    </Comp>
  );
};

Button.displayName = 'Button';

export { Button, buttonVariants };
